import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  MusicNote as MusicIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import { SongModel } from "../Entities/SongModel";
import { apiDeleteSong, apiFetchUserSongs } from "../utils/useDb";
import { useUser } from "../utils/useUser";
import LoginModal from "../Components/LoginModal";

export default function UserSongsPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();

  const [songs, setSongs] = useState<SongModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loginModalOpen, setLoginModalOpen] = useState<boolean>(false);

  // Deletion modal state
  const [songToDelete, setSongToDelete] = useState<SongModel | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const loadUserSongs = useCallback(async () => {
    if (!isAuthenticated) {
      setSongs([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const items = await apiFetchUserSongs(searchQuery.trim() || undefined);
      setSongs(items);
    } catch (err) {
      console.error("Error loading user songs:", err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, searchQuery]);

  useEffect(() => {
    loadUserSongs();
  }, [loadUserSongs]);

  const handleDeleteConfirm = async () => {
    if (!songToDelete) return;
    setIsDeleting(true);
    try {
      await apiDeleteSong(songToDelete.id);
      setSongToDelete(null);
      await loadUserSongs();
    } catch (err) {
      console.error("Failed to delete song:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const getDifficultyColor = (diff?: string) => {
    switch (diff) {
      case "beginner":
        return "success";
      case "intermediate":
        return "warning";
      case "advanced":
      case "expert":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", py: { xs: 2, sm: 3 } }}>
      <Container maxWidth="xl">
        {/* Top Header Bar */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          mb={3}
        >
          <Box>
            <Typography variant="h4" fontWeight="800" sx={{ letterSpacing: "-0.5px" }}>
              My Songs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your personal library of custom and edited songs
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              if (!isAuthenticated) {
                setLoginModalOpen(true);
              } else {
                navigate("/EditSong");
              }
            }}
            sx={{
              borderRadius: 2,
              px: 2.5,
              py: 0.9,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
            }}
          >
            Create Song
          </Button>
        </Box>

        {/* Not Authenticated State */}
        {!isAuthenticated ? (
          <Card
            variant="outlined"
            sx={{
              p: { xs: 3, sm: 6 },
              textAlign: "center",
              borderRadius: 3,
              bgcolor: "#ffffff",
            }}
          >
            <MusicIcon sx={{ fontSize: 54, color: "#94a3b8", mb: 2 }} />
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Sign in to manage your songs
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mx: "auto", mb: 3 }}>
              Create your own songs with custom chord fingerings, or edit copies of existing songs. Sign in to keep your personal library saved and private.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setLoginModalOpen(true)}
              sx={{ borderRadius: 2, px: 3, textTransform: "none", fontWeight: 600 }}
            >
              Sign In
            </Button>
            <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
          </Card>
        ) : (
          <>
            {/* Search filter bar */}
            <Box mb={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search your songs by title or artist..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  bgcolor: "#ffffff",
                  borderRadius: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            {/* Song Grid */}
            {isLoading ? (
              <Box py={8} textAlign="center">
                <Typography color="text.secondary">Loading your songs...</Typography>
              </Box>
            ) : songs.length > 0 ? (
              <Grid container spacing={2.5}>
                {songs.map((song) => {
                  const customChordCount = song.custom_chords
                    ? Object.keys(song.custom_chords).length
                    : 0;

                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={song.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 2.5,
                          borderTop: "4px solid #7c3aed",
                          bgcolor: "#ffffff",
                          transition: "0.2s",
                          "&:hover": {
                            boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <CardHeader
                          title={
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                              sx={{
                                fontSize: "1.05rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {song.title || "Untitled Song"}
                            </Typography>
                          }
                          subheader={
                            <Typography variant="body2" color="text.secondary">
                              {song.artist || "Unknown Artist"}
                            </Typography>
                          }
                          action={
                            <Chip
                              label="My Song"
                              size="small"
                              sx={{
                                bgcolor: "#f5f3ff",
                                color: "#7c3aed",
                                fontWeight: 700,
                                border: "1px solid #ddd6fe",
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                          }
                        />

                        <CardContent sx={{ pt: 0, flexGrow: 1 }}>
                          <Box display="flex" gap={0.75} flexWrap="wrap" mb={1.5}>
                            {song.difficulty && (
                              <Chip
                                label={song.difficulty}
                                size="small"
                                color={getDifficultyColor(song.difficulty)}
                              />
                            )}
                            {song.key && (
                              <Chip label={`Key: ${song.key}`} size="small" variant="outlined" />
                            )}
                            {song.capo && (
                              <Chip label={`Capo: ${song.capo}`} size="small" variant="outlined" />
                            )}
                            {customChordCount > 0 && (
                              <Chip
                                label={`${customChordCount} Custom Chord${customChordCount > 1 ? "s" : ""}`}
                                size="small"
                                sx={{
                                  bgcolor: "#f5f3ff",
                                  color: "#7c3aed",
                                  fontWeight: 600,
                                  fontSize: "0.72rem",
                                }}
                              />
                            )}
                          </Box>

                          {song.created_at && (
                            <Typography variant="caption" color="text.secondary">
                              Updated {new Date(song.updated_at || song.created_at).toLocaleDateString()}
                            </Typography>
                          )}
                        </CardContent>

                        <CardActions
                          sx={{
                            borderTop: "1px solid #f1f5f9",
                            px: 2,
                            py: 1,
                            justifyContent: "space-between",
                          }}
                        >
                          <Box display="flex" gap={1}>
                            <Button
                              component={Link}
                              to={`/Song?id=${song.id}`}
                              size="small"
                              startIcon={<ViewIcon sx={{ fontSize: 16 }} />}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              View
                            </Button>
                            <Button
                              component={Link}
                              to={`/EditSong?id=${song.id}`}
                              size="small"
                              color="primary"
                              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                              sx={{ textTransform: "none", fontWeight: 600 }}
                            >
                              Edit
                            </Button>
                          </Box>

                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Delete ${song.title}`}
                            onClick={() => setSongToDelete(song)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Card
                variant="outlined"
                sx={{
                  p: { xs: 4, sm: 6 },
                  textAlign: "center",
                  borderRadius: 3,
                  bgcolor: "#ffffff",
                }}
              >
                <MusicIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1.5 }} />
                <Typography variant="h6" fontWeight="bold">
                  {searchQuery ? "No matching songs" : "No user songs yet"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto", mb: 2.5 }}>
                  {searchQuery
                    ? "Try clearing your search query or check the explore page."
                    : "Create your first custom song or use the 'Edit yourself' button on any existing song."}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/EditSong")}
                  sx={{ borderRadius: 2, px: 2.5, textTransform: "none", fontWeight: 600 }}
                >
                  Create Song
                </Button>
              </Card>
            )}
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={Boolean(songToDelete)}
          onClose={() => !isDeleting && setSongToDelete(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle fontWeight="bold">Delete Song?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete <strong>{songToDelete?.title}</strong>? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              onClick={() => setSongToDelete(null)}
              disabled={isDeleting}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
              sx={{ textTransform: "none", borderRadius: 1.5 }}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
